// Notification Types for SkillZen Notification System

// ============== ENUMS ==============

export enum NotificationType {
  SYSTEM = 'SYSTEM',
  ASSIGNMENT = 'ASSIGNMENT',
  REMINDER = 'REMINDER',
  ACHIEVEMENT = 'ACHIEVEMENT',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  DEADLINE = 'DEADLINE',
}

export enum NotificationPriority {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum NotificationChannel {
  IN_APP = 'IN_APP',
  PUSH = 'PUSH',
  EMAIL = 'EMAIL',
}

export enum RecipientStatus {
  PENDING = 'PENDING',
  DELIVERED = 'DELIVERED',
  UNREAD = 'UNREAD',
  READ = 'READ',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum NotificationTargetType {
  USER = 'USER',
  GROUP = 'GROUP',
  ROLE = 'ROLE',
  ALL = 'ALL',
}

// ============== INTERFACES ==============

// Notification recipient tracking
export interface NotificationRecipient {
  user_id: string;
  status: RecipientStatus;
  delivered_at: string | null;
  read_at: string | null;
  push_sent: boolean;
  email_sent: boolean;
}

// Notification context (what triggered it)
export interface NotificationContext {
  entity_type: string;
  entity_id: string;
}

// Core notification interface
export interface Notification {
  _id: string;
  tenant_key: string;
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  channels: NotificationChannel[];
  target_type: NotificationTargetType;
  target_ids: string[];
  recipients: NotificationRecipient[];
  action_url: string | null;
  context: NotificationContext | null;
  scheduled_at: string | null;
  expires_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// User-facing notification (simplified for display)
export interface UserNotification {
  _id: string;
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  action_url: string | null;
  context: NotificationContext | null;
  status: RecipientStatus;
  read_at: string | null;
  is_toasted: boolean;
  created_at: string;
  profile_image?: string | null;
  // Push-only fields — populated by backend from the notification's
  // push_options when the notification was sent over PUSH. Used by the
  // user-facing list UI to render the push icon and TTL countdown for
  // push-channel notifications.
  channels?: NotificationChannel[];
  icon_url?: string | null;
  ttl?: number | null;
}

// ============== USER PREFERENCES ==============

export interface ChannelPreferences {
  in_app: {
    enabled: boolean;
  };
  push: {
    enabled: boolean;
  };
  email: {
    enabled: boolean;
  };
}

export interface NotificationPreferences {
  _id: string;
  tenant_key: string;
  user_id: string;
  channels: ChannelPreferences;
  created_at: string;
  updated_at: string;
}

// ============== API REQUEST/RESPONSE TYPES ==============

// Query params for listing notifications
export interface NotificationQueryParams {
  skip?: number;
  limit?: number;
  type?: NotificationType;
  status?: RecipientStatus;
  priority?: NotificationPriority;
  start_date?: string;
  end_date?: string;
}

// Create notification request (admin)
// Push-only customization options (maps to OneSignal fields in backend)
export interface PushOptions {
  image_url?: string;
  icon_url?: string;
  ttl?: number; // seconds, 60 to 2419200 (4 weeks). Default 259200 (3 days).
  subtitle?: string;
  collapse_id?: string;
  silent?: boolean;
}

/**
 * Tuple identifying a role within a department. Used as the element type
 * of target_ids when target_type is ROLE — roles are nested inside each
 * user's groups[] sub-array in the User document, so a role_key alone is
 * ambiguous when the same key appears under multiple departments. The
 * backend resolveTargetToUserIds expects this exact shape.
 */
export interface RoleAssignmentTuple {
  group_key: string;
  role_key: string;
}

export interface CreateNotificationRequest {
  title: string;
  message: string;
  type: NotificationType;
  priority?: NotificationPriority;
  channels: NotificationChannel[];
  target_type: NotificationTargetType;
  // Strings for USER/GROUP, RoleAssignmentTuple objects for ROLE, omit for ALL
  target_ids?: (string | RoleAssignmentTuple)[];
  action_url?: string;
  context?: NotificationContext;
  scheduled_at?: string;
  expires_at?: string;
  // Outlook-style email compose fields
  email_subject?: string;
  email_html_body?: string;
  cc_emails?: string[];
  bcc_emails?: string[];
  // OneSignal push customization
  push_options?: PushOptions;
}

// Push subscriber record returned by OneSignal (via backend).
// Each row = one device/session. Enriched by backend with user details when
// the external_id matches a user in our DB.
export interface PushSubscriber {
  external_id?: string;
  onesignal_id?: string;
  subscriptions?: Array<{
    id?: string;
    type?: string;
    enabled?: boolean;
    notification_types?: number;
    // Device fingerprint — same user on multiple devices = multiple rows,
    // distinguished by these fields. `platform` is a friendly label
    // (Windows / macOS / Android / iOS / etc.) derived backend-side.
    device_os?: string;
    device_model?: string;
    platform?: string;
  }>;
  // enrichment from Users collection
  user_name?: string;
  user_email?: string;
  user_employee_id?: string;
  user_profile_image?: string;
}

export interface PushSubscriberListResponse {
  subscribers: PushSubscriber[];
  total_count?: number;
}

// Schedule notification request (admin)
export interface ScheduleNotificationRequest extends CreateNotificationRequest {
  scheduled_at: string;
}

// Update preferences request
export interface UpdatePreferencesRequest {
  channels?: Partial<ChannelPreferences>;
}

// ============== API RESPONSE TYPES ==============

export interface NotificationListResponse {
  success: boolean;
  code: number;
  description: string;
  data: UserNotification[];
  meta_data: {
    total: number;
    skip: number;
    limit: number;
    unread_count: number;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  code: number;
  data: {
    unread_count: number;
  };
}

export interface NotificationActionResponse {
  success: boolean;
  code: number;
  description: string;
  data: {
    notification_id: string;
    status: RecipientStatus;
  };
}

export interface PreferencesResponse {
  success: boolean;
  code: number;
  data: NotificationPreferences;
}

// Admin notification history item
export interface RecipientDetail {
  user_id: string;
  user_name: string;
  user_email: string;
  employee_id: string;
  status: RecipientStatus;
  delivered_at: string | null;
  read_at: string | null;
  push_sent: boolean;
  email_sent: boolean;
  // Reason for FAILED rows. PREFERENCE_DISABLED → user opted out of channel.
  // DELIVERY_ERROR → infra-side failure. null for non-FAILED rows.
  failure_reason: 'PREFERENCE_DISABLED' | 'DELIVERY_ERROR' | null;
}

export interface NotificationRecipientsResponse {
  notification_id: string;
  title: string;
  recipients: RecipientDetail[];
}

export interface NotificationHistoryItem {
  _id: string;
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  channels: NotificationChannel[];
  target_type: NotificationTargetType;
  action_url: string | null;
  recipients_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  is_cancelled: boolean;
  cancelled_at: string | null;
  scheduled_at: string | null;
  created_at: string;
  created_by: string;
  created_by_name: string;
  created_by_email: string;
  created_by_employee_id: string;
  // Push-only fields — populated by backend from push_options when the
  // notification was sent over PUSH. Used to render the push icon image
  // and TTL details in the history list.
  icon_url?: string | null;
  ttl?: number | null;
}

export interface NotificationHistoryResponse {
  success: boolean;
  code: number;
  data: NotificationHistoryItem[];
  meta_data: {
    total: number;
    skip: number;
    limit: number;
  };
}

// ============== WEBSOCKET EVENT TYPES ==============

export interface WebSocketNotificationEvent {
  type: 'notification:new' | 'notification:read' | 'unread-count:updated';
  payload: any;
}

export interface NewNotificationPayload {
  notification: UserNotification;
}

export interface NotificationReadPayload {
  notification_id: string;
  read_at: string;
}

export interface UnreadCountPayload {
  unread_count: number;
}

// ============== UI STATE TYPES ==============

// Type guard for notification type icons
export const NotificationTypeConfig: Record<NotificationType, {
  icon: string;
  color: string;
  bgColor: string;
  label: string;
}> = {
  [NotificationType.SYSTEM]: {
    icon: 'Settings',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    label: 'System',
  },
  [NotificationType.ASSIGNMENT]: {
    icon: 'BookOpen',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'Assignment',
  },
  [NotificationType.REMINDER]: {
    icon: 'Clock',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    label: 'Reminder',
  },
  [NotificationType.ACHIEVEMENT]: {
    icon: 'Trophy',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    label: 'Achievement',
  },
  [NotificationType.ANNOUNCEMENT]: {
    icon: 'Megaphone',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    label: 'Announcement',
  },
  [NotificationType.DEADLINE]: {
    icon: 'AlertTriangle',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Deadline',
  },
};

// Priority configuration
export const NotificationPriorityConfig: Record<NotificationPriority, {
  color: string;
  bgColor: string;
  label: string;
}> = {
  [NotificationPriority.LOW]: {
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    label: 'Low',
  },
  [NotificationPriority.NORMAL]: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'Normal',
  },
  [NotificationPriority.HIGH]: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    label: 'High',
  },
  [NotificationPriority.URGENT]: {
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Urgent',
  },
};

// ============== SYSTEM NOTIFICATION CONFIGURATION ==============

export enum SystemNotificationModule {
  COURSE = 'COURSE',
  DEPARTMENT = 'DEPARTMENT',
  ROLE = 'ROLE',
  CATEGORY = 'CATEGORY',
  CERTIFICATE = 'CERTIFICATE',
  LEARNING_PATH = 'LEARNING_PATH',
}

export enum SystemNotificationRecipientMode {
  TRIGGER_USER = 'TRIGGER_USER',
  DEPARTMENT_ROLE = 'DEPARTMENT_ROLE',
  CUSTOM_USERS = 'CUSTOM_USERS',
}

export interface SystemNotificationChannels {
  in_app: boolean;
  email: boolean;
  push: boolean;
}

export interface SystemNotificationDepartmentRole {
  group_key: string;
  role_keys: string[];
}

export interface SystemNotificationRecipients {
  modes: SystemNotificationRecipientMode[];
  department_roles: SystemNotificationDepartmentRole[];
  user_ids: string[];
}

export interface SystemNotificationConfigItem {
  event_type: string;
  module: SystemNotificationModule;
  label: string;
  description: string;
  notification_type: NotificationType;
  priority: NotificationPriority;
  enabled: boolean;
  channels: SystemNotificationChannels;
  recipients: SystemNotificationRecipients;
  is_configured: boolean;
  order: number;
  updated_at?: string;
  updated_by?: string;
}

export interface SystemNotificationModuleGroup {
  module: SystemNotificationModule;
  label: string;
  order: number;
  enabled_count: number;
  events: SystemNotificationConfigItem[];
}

export interface SystemNotificationConfigList {
  modules: SystemNotificationModuleGroup[];
  total_events: number;
  total_enabled: number;
}

export interface UpsertSystemNotificationConfigRequest {
  enabled: boolean;
  channels: SystemNotificationChannels;
  recipients: SystemNotificationRecipients;
}
